/**
 * Groups the flat message list into render items so a full assistant turn
 * composes as one unit.
 *
 * Per maximal run of consecutive assistant messages (a "segment", delimited
 * by user/system turns):
 *   - the LAST assistant-text message is the final answer,
 *   - every earlier assistant-text message is commentary (folds into the
 *     turn's Thinking timeline),
 *   - tool-result cards (RAG cards, course browser) are collected separately.
 *
 * On qa a tool card is a `Message` with `result` set (a `MessageResult`
 * discriminated union: course XOR RAG); assistant text is a `Message` with a
 * non-empty `content` and no `result`.
 *
 * The page composes an `assistant-turn`: with a RAG card it renders one
 * hero→answer→sources bubble (commentary + refs also feed the Thinking
 * timeline); non-RAG turns render the cards + answer as inline blocks.
 */

import { MessageRole, type Message } from '@/types/chat';

export interface AssistantTurnItem {
  kind: 'assistant-turn';
  id: string;
  /** All-but-last assistant text — folds into the Thinking timeline. */
  commentary: Message[];
  /** Tool-result cards (RAG + course browser), in original order. */
  cards: Message[];
  /** The final answer text message (null mid-stream). */
  answer: Message | null;
  /** "Worked for Xs" span for a RESTORED turn, from persisted row timestamps:
   *  preceding user message → last pre-answer artifact (commentary/tool card,
   *  preferring a tool card's `completedAt` — the tool's END time — over its
   *  `createdAt` START time). That matches the LIVE semantics (working span,
   *  not answer streaming), which ThinkingBlock measures client-side instead —
   *  live tool cards and commentary carry no `createdAt`/`completedAt`, so
   *  this is null for live turns (and for legacy rows missing timestamps). */
  workedSeconds: number | null;
  /** Epoch-ms of the user message that opened the turn, when known. On a LIVE
   *  turn (sendText stamps createdAt) this anchors ThinkingBlock's "Worked
   *  for Xs" clock at the moment the user hit send — the timeline itself only
   *  mounts once the first commentary arrives, which can be many seconds
   *  later and would undercount. */
  turnStartedAt: number | null;
}

export type RenderItem =
  | { kind: 'message'; message: Message }
  | AssistantTurnItem;

function isToolCard(m: Message): boolean {
  return m.result !== undefined;
}

function isAssistantText(m: Message): boolean {
  return (
    m.role === MessageRole.ASSISTANT &&
    !isToolCard(m) &&
    typeof m.content === 'string' &&
    m.content.trim().length > 0
  );
}

export function groupMessagesForRender(messages: Message[]): RenderItem[] {
  const items: RenderItem[] = [];
  let i = 0;

  while (i < messages.length) {
    const head = messages[i];

    if (head.role !== MessageRole.ASSISTANT) {
      items.push({ kind: 'message', message: head });
      i++;
      continue;
    }

    // Collect a maximal run of consecutive assistant messages.
    let j = i;
    const seg: Message[] = [];
    while (j < messages.length && messages[j].role === MessageRole.ASSISTANT) {
      seg.push(messages[j]);
      j++;
    }

    const textIdxs = seg.reduce<number[]>((acc, m, k) => {
      if (isAssistantText(m)) acc.push(k);
      return acc;
    }, []);
    const lastTextIdx =
      textIdxs.length > 0 ? textIdxs[textIdxs.length - 1] : -1;

    // Classify commentary vs. the final answer by the backend `isFinal` flag
    // (response-scoping: a message whose response carried a function_call is
    // commentary → false; a message in a response with no function_call is the
    // answer → true). The Realtime API exposes no early "this is the answer"
    // signal — `is_final:true` is only provable at response.done — so live
    // placement is necessarily a guess that the flag later confirms or
    // corrects. The guess encodes the strongest context we have:
    //   - Text streamed BEFORE the turn's first tool result is commentary
    //     (the C8 dead-air filler rides the same response as the tool call —
    //     keep it in the Thinking timeline).
    //   - Text streamed AFTER a tool result is optimistically THE ANSWER —
    //     it streams straight into the prose surface instead of sitting in
    //     the Thinking timeline until response.done and then hopping out
    //     (the pre-2026-07 pessimistic default). A later `is_final:false`
    //     (rare chained second tool call) demotes it back — accepted
    //     residual, decided with the owner 2026-07-21.
    // For a RESTORED turn (no streaming, no flags) fall back to position.
    const live = seg.some((m) => m.streaming || m.isFinal !== undefined);
    const firstToolCardIdx = seg.findIndex(isToolCard);
    const isAnswerAt = (k: number): boolean => {
      const m = seg[k];
      if (m.isFinal === true) return true;
      if (m.isFinal === false) return false;
      if (live) {
        return (
          firstToolCardIdx !== -1 && k > firstToolCardIdx && k === lastTextIdx
        );
      }
      return k === lastTextIdx;
    };
    let answerIdx = -1;
    for (const k of textIdxs) if (isAnswerAt(k)) answerIdx = k;

    const commentary: Message[] = [];
    const cards: Message[] = [];
    let answer: Message | null = null;

    seg.forEach((m, k) => {
      if (k === answerIdx) {
        answer = m;
      } else if (isToolCard(m)) {
        cards.push(m);
      } else if (isAssistantText(m)) {
        commentary.push(m);
      } else {
        // Non-text, non-card assistant message (shouldn't normally occur) —
        // keep it in the card lane so it still renders rather than vanishing.
        cards.push(m);
      }
    });

    // Restored-turn worked span: the user message that opened the turn is the
    // message immediately before the segment (when it IS a user message — a
    // system notice in between forfeits the duration rather than mis-measuring).
    const opener = i > 0 ? messages[i - 1] : undefined;
    const openerTs =
      opener?.role === MessageRole.USER ? opener.createdAt : undefined;
    // Prefer a card's `completedAt` (tool END time) over its `createdAt`
    // (tool START time, on a restored tool-call row) as the last-pre-answer
    // bound — `createdAt` alone would undercount by the tool's own runtime.
    // Math.max across BOTH fields per message so a legacy row carrying only
    // `createdAt` still contributes cleanly.
    const artifactTs = seg
      .filter((m) => m !== answer)
      .flatMap((m) => [m.createdAt, m.completedAt])
      .filter((t): t is number => typeof t === 'number');
    const lastArtifactTs =
      artifactTs.length > 0 ? Math.max(...artifactTs) : undefined;
    const workedSeconds =
      openerTs !== undefined &&
      lastArtifactTs !== undefined &&
      lastArtifactTs >= openerTs
        ? (lastArtifactTs - openerTs) / 1000
        : null;

    items.push({
      kind: 'assistant-turn',
      id: `turn-${seg[0].id}`,
      commentary,
      cards,
      answer,
      workedSeconds,
      turnStartedAt: openerTs ?? null,
    });

    i = j;
  }

  return items;
}

/**
 * [Turn 18] Rested-row label: "Thought for 8s", "Thought for 1m 12s"
 * (replaces FB17's "Worked for Xs" wording — owner-refined in the 18a/18b
 * mocks). Sub-second spans round up to 1s — "Thought for 0s" reads as
 * broken; a null span (legacy rows without usable timestamps) falls back to
 * "Thought for a moment" so the row stays in the design's language instead
 * of showing a duration-less stub.
 */
export function formatThoughtLabel(seconds: number | null): string {
  if (seconds === null) return 'Thought for a moment';
  const total = Math.max(1, Math.round(seconds));
  if (total < 60) return `Thought for ${total}s`;
  const m = Math.floor(total / 60);
  const s = total % 60;
  return s === 0 ? `Thought for ${m}m` : `Thought for ${m}m ${s}s`;
}

/**
 * [Turn 18] The living status label next to the breathing orb while Sara
 * works. Per the mock's own rule (comment in 18a): a short present-tense
 * phrase naming the CURRENT phase, pulled from a small FIXED vocabulary
 * keyed to the action — never free-form — falling back to plain "Thinking"
 * when nothing specific applies. Keyed off the running tool (turn_state's
 * `tool`); pure reflection (no tool) has no reliable signal to name, so it
 * takes the designed fallback.
 */
const THINKING_STATUS_VOCABULARY: Record<string, string> = {
  browse_aol_content: 'Consulting Art of Living guidance',
  // Unified retrieval tool (P3) — same phrase as browse; missing from this
  // map after the rename left content searches on the generic fallback.
  search_aol_content: 'Consulting Art of Living guidance',
  fetch_quick_aol_content: 'Consulting Art of Living guidance',
  find_courses: 'Finding courses to share',
  ask_gurudev: "Consulting Gurudev's wisdom",
};

export function thinkingStatusLabel(liveTool: string | null): string {
  return (
    (liveTool !== null ? THINKING_STATUS_VOCABULARY[liveTool] : undefined) ??
    'Thinking'
  );
}
